/**
 * Generates a detailed table of cumulative ETF fills.
 * @param {Array} etfs - An array of ETF objects. Each ETF object must include:
 *   - symbol: The ETF ticker symbol.
 *   - weight: Weight of the ETF.
 *   - holdings: Array of holdings, each holding must include:
 *     - symbol: Stock symbol.
 *     - wgt: Weight of the stock in the ETF.
 * @returns {string[][]} detailedRows - The detailed table rows as an array of arrays of strings.
 */
function formatCumulativeEtfHoldings(etfs) {
    // Extract ETF symbols
    const etfSymbols = etfs.map((etf) => etf.symbol);

    // Calculate total holdings weight for each ETF and store ETF weights
    const { etfContributions, etfWeights } = calculateEtfContributions(etfs);

    // Consolidate stock weights across ETFs and calculate contributions
    const consolidatedStocks = consolidateStocks(etfs, etfContributions, etfWeights);

    // Sort stocks by consolidated weight in descending order
    const sortedStocks = Object.entries(consolidatedStocks).sort(
        (a, b) => b[1].totalWeight - a[1].totalWeight
    );

    // Build the detailedRows table with cumulative weights
    const detailedRows = buildDetailedRows(sortedStocks, etfSymbols, etfWeights);

    return detailedRows;
}

/**
 * Calculates total holdings weight for each ETF and ETF weights.
 * @param {Array} etfs - Array of ETF objects.
 * @returns {{ etfContributions: Object, etfWeights: Object }}
 */
function calculateEtfContributions(etfs) {
    if (!etfs?.length) return { etfContributions: {}, etfWeights: {} };
    const etfContributions = {};
    const etfWeights = {};

    etfs.forEach((etf) => {
        const etfSymbol = etf.symbol;
        etfWeights[etfSymbol] = etf.weight;

        const totalHoldingsWeight = etf?.holdings?.reduce?.(
            (sum, holding) => sum + holding.wgt,
            0
        ) ?? 0;
        etfContributions[etfSymbol] = totalHoldingsWeight;
    });

    return { etfContributions, etfWeights };
}

/**
 * Consolidates stock weights across ETFs and calculates contributions.
 * @param {Array} etfs - Array of ETF objects.
 * @param {Object} etfContributions - Total holdings weight per ETF.
 * @param {Object} etfWeights - Weights of each ETF.
 * @returns {Object} consolidatedStocks - Stocks consolidated across ETFs.
 */
function consolidateStocks(etfs, etfContributions, etfWeights) {
    if (!etfs?.length) return {};
    const consolidatedStocks = {};

    etfs.forEach((etf) => {
        const etfSymbol = etf.symbol;
        const etfWeight = etf.weight;
        if (!Array.isArray(etf.holdings)) return;
        etf.holdings.forEach((holding) => {
            const symbol = holding.symbol;
            const holdingWeight = holding.wgt;

            if (!consolidatedStocks[symbol]) {
                consolidatedStocks[symbol] = {
                    totalWeight: 0,
                    contributions: {},
                    parents: [],
                    sector: holding.sector,
                    industry: holding.industry,
                };
            }

            const stockWeight = holdingWeight * etfWeight;
            consolidatedStocks[symbol].totalWeight += stockWeight;
            consolidatedStocks[symbol].parents.push(etfSymbol);

            if (!consolidatedStocks[symbol].contributions[etfSymbol]) {
                consolidatedStocks[symbol].contributions[etfSymbol] = {
                    absolute: 0,
                    relative: 0,
                };
            }

            const absoluteContribution = holdingWeight / etfContributions[etfSymbol];
            consolidatedStocks[symbol].contributions[etfSymbol].absolute =
                absoluteContribution;
            consolidatedStocks[symbol].contributions[etfSymbol].relative =
                stockWeight;
        });
    });

    return consolidatedStocks;
}

/**
 * Builds the detailedRows table with cumulative weights.
 * @param {Array} sortedStocks - Array of [symbol, data] pairs sorted by totalWeight.
 * @param {Array} etfSymbols - List of ETF symbols.
 * @param {Object} etfWeights - Weights of each ETF.
 * @returns {string[][]} detailedRows - The detailed table rows.
 */
function buildDetailedRows(sortedStocks, etfSymbols, etfWeights) {
    const headers = ['Ticker', 'Weight', 'Total'];
    etfSymbols.forEach((etfSymbol) => {
        headers.push(`${etfSymbol} Abs`, `${etfSymbol} Rel`);
    });
    const detailedRows = [headers];

    const etfCumulativeWeights = {};
    etfSymbols.forEach((etfSymbol) => {
        etfCumulativeWeights[etfSymbol] = { absolute: 0, relative: 0 };
    });

    let cumulativeWeight = 0;

    sortedStocks.forEach(([symbol, data]) => {
        const stockTotalWeight = data.totalWeight;
        cumulativeWeight += stockTotalWeight;
        const row = [
            symbol,
            stockTotalWeight.toFixed(4),
            cumulativeWeight.toFixed(4),
        ];

        etfSymbols.forEach((etfSymbol) => {
            const contributions = data.contributions[etfSymbol] || {
                absolute: 0,
                relative: 0,
            };
            etfCumulativeWeights[etfSymbol].absolute += contributions.absolute;
            etfCumulativeWeights[etfSymbol].relative +=
                contributions.relative / etfWeights[etfSymbol];
            row.push(
                etfCumulativeWeights[etfSymbol].absolute.toFixed(4),
                etfCumulativeWeights[etfSymbol].relative.toFixed(4)
            );
        });

        detailedRows.push(row);
    });

    return detailedRows;
}

export default formatCumulativeEtfHoldings;
